import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faCrown, faGauge } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { AreaTableShape, AreaTableView, AreaView, FloorDecorElement, FloorDecorKind } from '../../../../services/areas/areas.service';
import type { OrderView } from '../../../../services/orders/orders.service';
import type { AlignmentGapHints, AlignmentGuides, ResizeHandle } from '../../core/pos-shell-page.types';
import { clamp } from '../../core/pos-shell-page.helpers';
import type { TransferMode, MergeMode } from '../../hooks/usePosOrdersDomain';
import type { MergeLink } from '../../../../services/merge/merge.service';
import { AreaBar } from './area-bar/AreaBar';
import { AddTablePalette } from './add-table-palette/AddTablePalette';
import { TableEditToolbar } from './table-edit-toolbar/TableEditToolbar';
import { SetMinimumOrderModal, type MinimumOrderPayload } from '../settings/set-minimum-order/SetMinimumOrderModal';
import { resolveEffectiveMinimumOrder } from 'shared-models/browser';
import { formatMoneyCompact } from '../../../../utils/money';
import { TableLongPressMenu } from '../transfer/table-long-press-menu/TableLongPressMenu';
import { SplitMergeModal } from '../transfer/split-merge-modal/SplitMergeModal';
import { MergeConflictModal } from '../transfer/merge-conflict-modal/MergeConflictModal';
import layoutClasses from '../../pos-shell-page.module.scss';
import transferClasses from '../transfer/shared/transfer-areas.module.scss';

const LONG_PRESS_MS = 500;
type FloorBackground = 'white' | 'image' | 'primary';
type DecorInteractionMode = 'move' | 'resize-start' | 'resize-end' | 'rotate';
type DecorInteractionState = {
  pointerId: number;
  areaId: number;
  decorId: string;
  mode: DecorInteractionMode;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  startRotation: number;
  centerX: number;
  centerY: number;
  startAngleDeg: number;
  axisX: number;
  axisY: number;
  anchorX: number;
  anchorY: number;
  handleStartX: number;
  handleStartY: number;
  startRenderHeight: number;
};

const normalizeRotationDeg = (value: number): number => {
  const wrapped = ((value + 180) % 360 + 360) % 360 - 180;
  return Number.isFinite(wrapped) ? wrapped : 0;
};

const snapRotationDeg = (value: number): number => {
  const normalized = normalizeRotationDeg(value);
  const coarseStep = 15;
  const nearest = Math.round(normalized / coarseStep) * coarseStep;
  return Math.abs(normalized - nearest) <= 5 ? nearest : normalized;
};

type PosAreasSectionProps = {
  tenantId: string;
  floorBackground?: FloorBackground;
  onFloorBackgroundChange: (nextFloorBackground: FloorBackground) => void;
  selectedAreaList: AreaView[];
  selectedAreaId: number | null;
  onSelectArea: (areaId: number) => void;
  areaMode: 'view' | 'edit';
  isManager: boolean;
  viewArea: AreaView | null;
  isLoadingAreas: boolean;
  viewCanvasHostRef: (node: HTMLDivElement | null) => void;
  viewAreaCanvasBounds: { width: number; height: number };
  viewAreaCanvasScale: number;
  activeOrders: OrderView[];
  onViewTableClick: (tableId: number, tableLabel: string) => void;
  onOpenTakeAway?: () => void;
  onOpenQuickOrder?: () => void;
  editingTableId: number | null;
  selectedEditTableIds?: number[];
  onSelectEditTable?: (tableId: number, additive: boolean) => void;
  onSelectEditTables?: (tableIds: number[], additive: boolean) => void;
  isSizeMasterPickMode?: boolean;
  onToggleSizeMasterPickMode?: () => void;
  onPickSizeMasterTable?: (tableId: number) => void;
  onAlignSelectedTablesRow?: () => void;
  onAlignSelectedTablesColumn?: () => void;
  editArea: AreaView | null;
  onToggleAreaActive: (area: AreaView) => void;
  editCanvasHostRef: (node: HTMLDivElement | null) => void;
  editAreaCanvasBounds: { width: number; height: number };
  alignmentGuides?: AlignmentGuides;
  alignmentGapHints?: AlignmentGapHints;
  onBeginCanvasInteraction: (
    event: React.MouseEvent<HTMLElement>,
    areaId: number,
    table: AreaTableView,
    mode: 'move' | 'resize',
    handle?: ResizeHandle,
  ) => void;
  onStartEditTable: (table: AreaTableView) => void;
  onExitEditMode: () => void;
  onDeleteTable: () => void;
  onRenameTable: (name: string) => void;
  onChangeShape: (shape: AreaTableView['shape']) => void;
  onDuplicateTable: () => void;
  onAddArea: (name: string) => void;
  onRenameArea: (areaId: number, name: string) => void;
  onUpdateAreaFloorDecor?: (areaId: number, floorDecor: FloorDecorElement[]) => void;
  onUpdateAreaFireApproval?: (areaId: number, requiresFireApproval: boolean) => void;
  onExportArea?: (areaId: number) => void;
  onImportArea?: (file: File) => void;
  onReorderAreas: (areaId: number, otherAreaId: number) => void;
  onAddTable: (number: string, shape: AreaTableShape, positionX: number, positionY: number, width: number, height: number) => void;
  transferMode?: TransferMode;
  onTransferTableLongPress?: (tableId: number, tableLabel: string, orderId: number) => void;
  onCancelTransfer?: () => void;
  onCancelTableLongPress?: (tableId: number, tableLabel: string, orderId: number) => void;
  onPrintBillLongPress?: (orderId: number) => void;
  onItemActionsLongPress?: (orderId: number) => void;
  mergeMode?: MergeMode;
  activeMergeLinks?: MergeLink[];
  onMergeTableLongPress?: (tableId: number, tableLabel: string, orderId: number | null) => void;
  onTableHistoryLongPress?: (tableId: number, tableLabel: string) => void;
  restorePickActive?: boolean;
  onPickRestoreTable?: (tableId: number, tableLabel: string) => void;
  onCancelRestorePick?: () => void;
  onCancelMerge?: () => void;
  onToggleMergeTable?: (tableId: number, tableLabel: string) => void;
  onConfirmMerge?: () => void;
  onPickTargetTable?: (tableId: number, tableLabel: string) => void;
  splitMergeOrderPk?: number | null;
  isSplittingMerge?: boolean;
  onBeginSplitMerge?: (tableId: number) => void;
  onCancelSplitMerge?: () => void;
  onConfirmSplitMerge?: (linkIds: number[], tables: { tableId: number; tableLabel: string; linkId: number }[]) => void;
  mergeConflict?: { hasPercentDiscount: boolean; hasPercentTip: boolean } | null;
  onMergeConflictCancel?: () => void;
  onMergeConflictConfirm?: () => void;
  isMergeConflictLoading?: boolean;
  setMinimumMode?: boolean;
  onExitSetMinimum?: () => void;
  onSaveTableMinimum?: (areaId: number, tableId: number, payload: MinimumOrderPayload) => Promise<boolean> | void;
  isSavingMinimum?: boolean;
};

const PosAreasSectionComponent: React.FC<PosAreasSectionProps> = ({
  floorBackground = 'white',
  onFloorBackgroundChange,
  selectedAreaList,
  selectedAreaId,
  onSelectArea,
  areaMode,
  isManager,
  viewArea,
  isLoadingAreas,
  viewCanvasHostRef,
  viewAreaCanvasBounds,
  viewAreaCanvasScale,
  activeOrders,
  onViewTableClick,
  onOpenTakeAway,
  onOpenQuickOrder,
  editingTableId,
  selectedEditTableIds = [],
  onSelectEditTable,
  onSelectEditTables,
  isSizeMasterPickMode = false,
  onToggleSizeMasterPickMode,
  onPickSizeMasterTable,
  onAlignSelectedTablesRow,
  onAlignSelectedTablesColumn,
  editArea,
  onToggleAreaActive,
  editCanvasHostRef,
  editAreaCanvasBounds,
  alignmentGuides = { vertical: null, horizontal: null },
  alignmentGapHints = { horizontal: null, vertical: null },
  onBeginCanvasInteraction,
  onStartEditTable,
  onExitEditMode,
  onDeleteTable,
  onRenameTable,
  onChangeShape,
  onDuplicateTable,
  onAddArea,
  onRenameArea,
  onUpdateAreaFloorDecor,
  onUpdateAreaFireApproval,
  onExportArea,
  onImportArea,
  onReorderAreas,
  onAddTable,
  transferMode = { step: 'idle' },
  onTransferTableLongPress,
  onCancelTransfer,
  onCancelTableLongPress,
  onPrintBillLongPress,
  onItemActionsLongPress,
  mergeMode = { step: 'idle' },
  activeMergeLinks = [],
  onMergeTableLongPress,
  onTableHistoryLongPress,
  restorePickActive = false,
  onPickRestoreTable,
  onCancelRestorePick,
  onCancelMerge,
  onToggleMergeTable,
  onConfirmMerge,
  onPickTargetTable,
  splitMergeOrderPk = null,
  isSplittingMerge = false,
  onBeginSplitMerge,
  onCancelSplitMerge,
  onConfirmSplitMerge,
  mergeConflict = null,
  onMergeConflictCancel,
  onMergeConflictConfirm,
  isMergeConflictLoading = false,
  setMinimumMode = false,
  onExitSetMinimum,
  onSaveTableMinimum,
  isSavingMinimum = false,
}) => {
  const [minTarget, setMinTarget] = useState<AreaTableView | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    tableId: number;
    tableLabel: string;
    orderId: number | null;
    style: React.CSSProperties;
  } | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justOpenedMenuRef = useRef(false);

  // Toolbar opens on double-click only; long-press/drag is reserved for moving the table.
  const [toolbarTableId, setToolbarTableId] = useState<number | null>(null);
  const [tableSelectionBox, setTableSelectionBox] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    additive: boolean;
    originLeft: number;
    originTop: number;
  } | null>(null);
  const tableSelectionDraggingRef = useRef(false);
  const suppressNextCanvasClickRef = useRef(false);

  const [isGridVisible, setIsGridVisible] = useState(false);
  const [decorDraftAreaId, setDecorDraftAreaId] = useState<number | null>(null);
  const [decorDraft, setDecorDraft] = useState<FloorDecorElement[]>([]);
  const [selectedDecorId, setSelectedDecorId] = useState<string | null>(null);
  const [decorToolbarId, setDecorToolbarId] = useState<string | null>(null);
  const decorInteractionRef = useRef<DecorInteractionState | null>(null);
  const decorInteractionItemsRef = useRef<FloorDecorElement[]>([]);
  const currentAreaDecorRef = useRef<FloorDecorElement[]>([]);

  // Add-table palette: armed number+shape place on the next canvas click.
  const [addTableNumber, setAddTableNumber] = useState('');
  const [addTableShape, setAddTableShape] = useState<AreaTableShape | null>(null);

  useEffect(() => {
    if (!tableSelectionBox || areaMode !== 'edit') {
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      setTableSelectionBox((previous) => {
        if (!previous) {
          return previous;
        }
        const moved = Math.abs(event.clientX - previous.startX) > 3 || Math.abs(event.clientY - previous.startY) > 3;
        if (moved) {
          tableSelectionDraggingRef.current = true;
        }
        return {
          ...previous,
          currentX: event.clientX - previous.originLeft,
          currentY: event.clientY - previous.originTop,
        };
      });
    };

    const onMouseUp = () => {
      setTableSelectionBox((previous) => {
        if (!previous) {
          return previous;
        }
        if (!tableSelectionDraggingRef.current) {
          tableSelectionDraggingRef.current = false;
          return null;
        }

        const rectLeft = Math.min(previous.startX, previous.currentX);
        const rectRight = Math.max(previous.startX, previous.currentX);
        const rectTop = Math.min(previous.startY, previous.currentY);
        const rectBottom = Math.max(previous.startY, previous.currentY);
        const selectedIds = (Array.isArray(editArea?.tables) ? editArea.tables : [])
          .filter((table) => {
            const tableLeft = table.positionX;
            const tableRight = table.positionX + table.width;
            const tableTop = table.positionY;
            const tableBottom = table.positionY + table.height;
            return tableLeft < rectRight && tableRight > rectLeft && tableTop < rectBottom && tableBottom > rectTop;
          })
          .map((table) => table.id);

        onSelectEditTables?.(selectedIds, previous.additive);
        suppressNextCanvasClickRef.current = true;
        tableSelectionDraggingRef.current = false;
        return null;
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [areaMode, editArea, onSelectEditTables, tableSelectionBox]);

  const currentAreaSource = areaMode === 'edit' ? editArea : viewArea;
  const currentAreaId = currentAreaSource?.id ?? null;
  const currentAreaDecorFromProps = useMemo(() => {
    return Array.isArray(currentAreaSource?.floorDecor) ? currentAreaSource.floorDecor : [];
  }, [currentAreaSource]);

  useEffect(() => {
    setDecorDraftAreaId(currentAreaId);
    setDecorDraft(currentAreaDecorFromProps);
  }, [currentAreaId, currentAreaDecorFromProps]);

  const currentAreaDecor = useMemo(() => {
    if (currentAreaId === null) return [];
    if (decorDraftAreaId === currentAreaId) {
      return decorDraft;
    }
    return currentAreaDecorFromProps;
  }, [currentAreaId, decorDraftAreaId, decorDraft, currentAreaDecorFromProps]);

  useLayoutEffect(() => {
    currentAreaDecorRef.current = currentAreaDecor;
  }, [currentAreaDecor]);

  const selectedDecor = useMemo(
    () => currentAreaDecor.find((item) => item.id === selectedDecorId) ?? null,
    [currentAreaDecor, selectedDecorId],
  );

  const toolbarDecor = useMemo(
    () => currentAreaDecor.find((item) => item.id === decorToolbarId) ?? null,
    [currentAreaDecor, decorToolbarId],
  );

  useEffect(() => {
    if (!selectedDecorId) return;
    if (!currentAreaDecor.some((item) => item.id === selectedDecorId)) {
      setSelectedDecorId(null);
    }
  }, [currentAreaDecor, selectedDecorId]);

  useEffect(() => {
    if (!decorToolbarId) return;
    if (!currentAreaDecor.some((item) => item.id === decorToolbarId)) {
      setDecorToolbarId(null);
    }
  }, [currentAreaDecor, decorToolbarId]);

  const mutateCurrentAreaDecor = useCallback((
    updater: (items: FloorDecorElement[]) => FloorDecorElement[],
    options?: { persist?: boolean },
  ) => {
    if (currentAreaId === null) return;
    const next = updater(currentAreaDecorRef.current);
    setDecorDraftAreaId(currentAreaId);
    setDecorDraft(next);
    currentAreaDecorRef.current = next;
    if (options?.persist !== false) {
      onUpdateAreaFloorDecor?.(currentAreaId, next);
    }
  }, [currentAreaId, onUpdateAreaFloorDecor]);

  const addDecorElement = (kind: FloorDecorKind) => {
    if (currentAreaId === null) return;
    const id = `decor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const created: FloorDecorElement = {
      id,
      kind,
      x: 80,
      y: 80,
      width: 220,
      height: 24,
      color: '#1e9b5a',
      textColor: '#0f172a',
      text: '',
      stroke: 4,
      fontSize: 18,
      rotation: 0,
    };
    mutateCurrentAreaDecor((items) => [...items, created]);
    setSelectedDecorId(id);
  };

  const updateDecorElement = (decorId: string, patch: Partial<FloorDecorElement>) => {
    mutateCurrentAreaDecor((items) => items.map((item) => (item.id === decorId ? { ...item, ...patch } : item)));
  };

  const removeDecorElement = (decorId: string) => {
    mutateCurrentAreaDecor((items) => items.filter((item) => item.id !== decorId));
    if (selectedDecorId === decorId) {
      setSelectedDecorId(null);
    }
    if (decorToolbarId === decorId) {
      setDecorToolbarId(null);
    }
  };

  const decorToolbarStyle = useMemo((): React.CSSProperties | null => {
    if (!toolbarDecor) {
      return null;
    }
    const toolbarWidth = 280;
    const toolbarHeight = 148;
    const decorBottom = toolbarDecor.y + Math.max(toolbarDecor.height, toolbarDecor.stroke + 8);
    const left = clamp(
      toolbarDecor.x + toolbarDecor.width / 2 - toolbarWidth / 2,
      8,
      Math.max(8, editAreaCanvasBounds.width - toolbarWidth - 8),
    );
    const canPlaceBelow = decorBottom + 12 + toolbarHeight <= editAreaCanvasBounds.height - 8;
    const preferredBelowTop = decorBottom + 12;
    const preferredAboveTop = toolbarDecor.y - toolbarHeight - 12;
    const top = canPlaceBelow
      ? preferredBelowTop
      : preferredAboveTop >= 8
        ? preferredAboveTop
        : clamp(preferredBelowTop, 8, Math.max(8, editAreaCanvasBounds.height - toolbarHeight - 8));
    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${toolbarWidth}px`,
    };
  }, [toolbarDecor, editAreaCanvasBounds.height, editAreaCanvasBounds.width]);

  const beginDecorInteraction = (
    event: React.PointerEvent,
    item: FloorDecorElement,
    mode: DecorInteractionMode,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.button !== 0) {
      return;
    }
    if (currentAreaId === null) {
      return;
    }
    const startWidth = Math.max(24, item.width);
    const startHeight = Math.max(12, item.height);
    const startStroke = Math.max(1, item.stroke);
    const startRenderHeight = Math.max(startHeight, startStroke + 8);
    const centerX = item.x + startWidth / 2;
    const centerY = item.y + startRenderHeight / 2;
    const rotationRad = (item.rotation * Math.PI) / 180;
    const leftX = centerX - Math.cos(rotationRad) * (startWidth / 2);
    const leftY = centerY - Math.sin(rotationRad) * (startWidth / 2);
    const rightX = centerX + Math.cos(rotationRad) * (startWidth / 2);
    const rightY = centerY + Math.sin(rotationRad) * (startWidth / 2);
    const isResizingStart = mode === 'resize-start';
    const anchorX = isResizingStart ? rightX : leftX;
    const anchorY = isResizingStart ? rightY : leftY;
    const handleStartX = isResizingStart ? leftX : rightX;
    const handleStartY = isResizingStart ? leftY : rightY;
    const startAngleDeg = (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) / Math.PI;
    setSelectedDecorId(item.id);
    // Snapshot from the currently rendered area data to avoid stale cross-area refs.
    decorInteractionItemsRef.current = currentAreaDecor;
    decorInteractionRef.current = {
      pointerId: event.pointerId,
      areaId: currentAreaId,
      decorId: item.id,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: item.x,
      startY: item.y,
      startWidth,
      startHeight,
      startRotation: item.rotation,
      centerX,
      centerY,
      startAngleDeg,
      axisX: Math.cos(rotationRad),
      axisY: Math.sin(rotationRad),
      anchorX,
      anchorY,
      handleStartX,
      handleStartY,
      startRenderHeight,
    };
  };

  useEffect(() => {
    if (areaMode !== 'edit' || !isManager) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const state = decorInteractionRef.current;
      if (!state) {
        return;
      }
      if (event.pointerId !== state.pointerId) {
        return;
      }
      const deltaX = event.clientX - state.startClientX;
      const deltaY = event.clientY - state.startClientY;
      const nextItems = decorInteractionItemsRef.current.map((item) => {
        if (item.id !== state.decorId) {
          return item;
        }
        if (state.mode === 'move') {
          return {
            ...item,
            x: clamp(state.startX + deltaX, 0, 5000),
            y: clamp(state.startY + deltaY, 0, 5000),
          };
        }
        if (state.mode === 'resize-end' || state.mode === 'resize-start') {
          const nextHandleXRaw = state.handleStartX + deltaX;
          const nextHandleYRaw = state.handleStartY + deltaY;
          let vectorX = nextHandleXRaw - state.anchorX;
          let vectorY = nextHandleYRaw - state.anchorY;
          let distance = Math.hypot(vectorX, vectorY);
          if (distance < 24) {
            const fallbackX = state.axisX === 0 ? 1 : state.axisX;
            const fallbackY = state.axisY;
            const unitX = distance > 0.0001 ? vectorX / distance : fallbackX;
            const unitY = distance > 0.0001 ? vectorY / distance : fallbackY;
            vectorX = unitX * 24;
            vectorY = unitY * 24;
            distance = 24;
          }

          const handleX = state.anchorX + vectorX;
          const handleY = state.anchorY + vectorY;
          const leftX = state.mode === 'resize-start' ? handleX : state.anchorX;
          const leftY = state.mode === 'resize-start' ? handleY : state.anchorY;
          const rightX = state.mode === 'resize-start' ? state.anchorX : handleX;
          const rightY = state.mode === 'resize-start' ? state.anchorY : handleY;
          const centerX = (leftX + rightX) / 2;
          const centerY = (leftY + rightY) / 2;
          const nextRotation = normalizeRotationDeg((Math.atan2(rightY - leftY, rightX - leftX) * 180) / Math.PI);

          return {
            ...item,
            x: clamp(centerX - distance / 2, 0, 5000),
            y: clamp(centerY - state.startRenderHeight / 2, 0, 5000),
            width: distance,
            rotation: nextRotation,
          };
        }
        if (state.mode === 'rotate') {
          const currentAngleDeg = (Math.atan2(event.clientY - state.centerY, event.clientX - state.centerX) * 180) / Math.PI;
          const deltaAngle = currentAngleDeg - state.startAngleDeg;
          const nextRotation = snapRotationDeg(state.startRotation + deltaAngle);
          return {
            ...item,
            rotation: Number.isFinite(nextRotation) ? nextRotation : item.rotation,
          };
        }
        return item;
      });
      decorInteractionItemsRef.current = nextItems;
      if (state.areaId === currentAreaId) {
        setDecorDraftAreaId(state.areaId);
        setDecorDraft(nextItems);
        currentAreaDecorRef.current = nextItems;
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const state = decorInteractionRef.current;
      if (!state || event.pointerId !== state.pointerId) {
        return;
      }
      decorInteractionRef.current = null;
      onUpdateAreaFloorDecor?.(state.areaId, decorInteractionItemsRef.current);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [areaMode, currentAreaId, isManager, onUpdateAreaFloorDecor]);

  const renderDecorElement = (item: FloorDecorElement) => {
    const safeWidth = Math.max(12, item.width);
    const safeHeight = Math.max(12, item.height);
    const stroke = Math.max(1, item.stroke);
    const renderHeight = Math.max(safeHeight, stroke + 8);
    const isEditingDecor = areaMode === 'edit' && isManager;
    const isSelected = selectedDecorId === item.id;
    const kind: FloorDecorKind = item.kind === 'arrow' ? 'arrow' : 'line';

    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${item.x}px`,
      top: `${item.y}px`,
      width: `${safeWidth}px`,
      height: `${renderHeight}px`,
      transform: `rotate(${item.rotation}deg)`,
      transformOrigin: 'center center',
      pointerEvents: isEditingDecor ? 'auto' : 'none',
      userSelect: 'none',
      touchAction: 'none',
    };

    const y = renderHeight / 2;
    const arrowHead = Math.max(10, Math.min(80, Math.max(stroke * 1.2, renderHeight * 0.75)));
    const endX = kind === 'arrow' ? safeWidth - arrowHead : safeWidth;

    return (
      <div
        key={item.id}
        data-testid={`pos-areas-decor-${item.id}`}
        className={`${layoutClasses.floorDecorItem} ${isSelected ? layoutClasses.floorDecorItemSelected : ''}`}
        style={baseStyle}
        onPointerDown={isEditingDecor ? (event) => beginDecorInteraction(event, item, 'move') : undefined}
        role={isEditingDecor ? 'button' : undefined}
        tabIndex={isEditingDecor ? 0 : -1}
        onClick={isEditingDecor ? (event) => { event.stopPropagation(); setSelectedDecorId(item.id); } : undefined}
        onDoubleClick={isEditingDecor ? (event) => {
          event.stopPropagation();
          setSelectedDecorId(item.id);
          setDecorToolbarId((current) => (current === item.id ? null : item.id));
        } : undefined}
      >
        <svg
          className={layoutClasses.floorDecorSvg}
          viewBox={`0 0 ${safeWidth} ${renderHeight}`}
          preserveAspectRatio="none"
        >
          <line
            x1="0"
            y1={y}
            x2={endX}
            y2={y}
            stroke={item.color}
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          {kind === 'arrow' && (
            <polygon
              points={`${safeWidth},${y} ${safeWidth - arrowHead},${y - arrowHead / 2} ${safeWidth - arrowHead},${y + arrowHead / 2}`}
              fill={item.color}
            />
          )}
          {item.text && (
            <text
              x={safeWidth / 2}
              y={y}
              dominantBaseline="middle"
              textAnchor="middle"
              fill={item.textColor ?? item.color}
              fontSize={Math.max(10, item.fontSize)}
              fontWeight="700"
            >
              {item.text}
            </text>
          )}
        </svg>
        {isEditingDecor && isSelected && (
          <>
            <span
              className={`${layoutClasses.floorDecorHandle} ${layoutClasses.floorDecorHandleRotate}`}
              onPointerDown={(event) => beginDecorInteraction(event, item, 'rotate')}
            />
            <span
              className={`${layoutClasses.floorDecorHandle} ${layoutClasses.floorDecorHandleStart}`}
              onPointerDown={(event) => beginDecorInteraction(event, item, 'resize-start')}
            />
            <span
              className={`${layoutClasses.floorDecorHandle} ${layoutClasses.floorDecorHandleEnd}`}
              onPointerDown={(event) => beginDecorInteraction(event, item, 'resize-end')}
            />
          </>
        )}
      </div>
    );
  };

  const handleEditTableDoubleClick = (table: AreaTableView) => {
    setToolbarTableId((current) => (current === table.id ? null : table.id));
  };

  const handleTablePointerDown = (
    event: React.PointerEvent,
    table: AreaTableView,
    orderId: number | null,
  ) => {
    if (event.button !== 0) return;
    // capture position values synchronously — do not read DOM inside the timeout
    const tableTop = table.positionY;
    const tableLeft = table.positionX;
    const tableWidth = table.width;
    const tableHeight = table.height;
    longPressTimerRef.current = setTimeout(() => {
      // place menu below center of the table in canvas-coordinate space
      justOpenedMenuRef.current = true;
      setContextMenu({
        tableId: table.id,
        tableLabel: table.number,
        orderId,
        style: {
          top: tableTop + tableHeight + 6,
          left: tableLeft + tableWidth / 2,
        },
      });
    }, LONG_PRESS_MS);
  };

  const handleTablePointerUp = (event: React.PointerEvent) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (justOpenedMenuRef.current) {
      // swallow the click that the browser synthesizes after pointerUp
      const el = event.currentTarget;
      const swallow = (e: Event) => { e.stopPropagation(); e.preventDefault(); el.removeEventListener('click', swallow, true); };
      el.addEventListener('click', swallow, true);
    }
  };

  // Merged satellites carry no direct order — fall back to the merge link's orderPk.
  const contextOrderId = contextMenu
    ? activeMergeLinks.find((link) => link.tablePk === contextMenu.tableId)?.orderPk ?? contextMenu.orderId
    : null;
  const contextOrder = contextOrderId !== null
    ? activeOrders.find((entry) => entry.id === contextOrderId) ?? null
    : null;
  const contextOrderHasSentLines = contextOrder?.hasSentLines ?? false;

  const handleContextMenuTransfer = () => {
    if (!contextMenu || !onTransferTableLongPress || contextOrderId === null) return;
    if (!contextOrderHasSentLines) {
      notifications.show({ color: 'blue', title: 'אין פריטים', message: 'אין פריטים להעברה בשולחן זה', autoClose: 3000 });
      setContextMenu(null);
      return;
    }
    onTransferTableLongPress(contextMenu.tableId, contextMenu.tableLabel, contextOrderId);
    setContextMenu(null);
  };

  const handleContextMenuMerge = () => {
    if (!contextMenu || !onMergeTableLongPress) return;
    onMergeTableLongPress(contextMenu.tableId, contextMenu.tableLabel, contextMenu.orderId);
    setContextMenu(null);
  };

  const handleContextMenuSplit = () => {
    if (!contextMenu || !onBeginSplitMerge) return;
    onBeginSplitMerge(contextMenu.tableId);
    setContextMenu(null);
  };

  const handleContextMenuCancelTable = () => {
    if (!contextMenu || !onCancelTableLongPress || contextOrderId === null) return;
    if (!contextOrderHasSentLines) {
      notifications.show({ color: 'blue', title: 'אין פריטים', message: 'אין פריטים לביטול בשולחן זה', autoClose: 3000 });
      setContextMenu(null);
      return;
    }
    void onCancelTableLongPress(contextMenu.tableId, contextMenu.tableLabel, contextOrderId);
    setContextMenu(null);
  };

  const handleContextMenuTableHistory = () => {
    if (!contextMenu || !onTableHistoryLongPress) return;
    onTableHistoryLongPress(contextMenu.tableId, contextMenu.tableLabel);
    setContextMenu(null);
  };

  const handleContextMenuPrintBill = () => {
    if (contextOrderId === null || !onPrintBillLongPress) return;
    onPrintBillLongPress(contextOrderId);
    setContextMenu(null);
  };

  const handleContextMenuItemActions = () => {
    if (contextOrderId === null || !onItemActionsLongPress) return;
    onItemActionsLongPress(contextOrderId);
    setContextMenu(null);
  };

  const isPickTableStep = transferMode.step === 'pick-table';
  const isRestorePickStep = restorePickActive;
  const sourceTableIds = isPickTableStep
    ? transferMode.sourceTableIds ?? (transferMode.sourceTableId === null ? [] : [transferMode.sourceTableId])
    : [];

  const isMergePickStep = mergeMode.step === 'pick-tables';
  const mergeSelectedIds = isMergePickStep ? mergeMode.selectedTableIds : [];
  const mergeCentralTableId = isMergePickStep ? mergeMode.centralTableId : null;
  const mergeCount = mergeSelectedIds.length;

  // Build tablePk → chain label map from active merge links + area table numbers
  const mergeChainLabelByTableId = useMemo(() => {
    if (activeMergeLinks.length === 0) return new Map<number, string>();

    // group links by orderPk
    const byOrder = new Map<number, number[]>();
    for (const link of activeMergeLinks) {
      const group = byOrder.get(link.orderPk) ?? [];
      group.push(link.tablePk);
      byOrder.set(link.orderPk, group);
    }

    // build a tablePk→number lookup from all areas (we only have viewArea here, so use it)
    const tableNumberById = new Map<number, string>();
    if (viewArea) {
      for (const t of viewArea.tables) {
        tableNumberById.set(t.id, t.number);
      }
    }

    const result = new Map<number, string>();
    for (const tablePks of byOrder.values()) {
      if (tablePks.length < 2) continue; // single table is not a real merge chain
      const label = tablePks
        .map((pk) => tableNumberById.get(pk) ?? String(pk))
        .join('-');
      for (const pk of tablePks) {
        result.set(pk, label);
      }
    }
    return result;
  }, [activeMergeLinks, viewArea]);

  const buildScaledCanvasStyle = (bounds: { width: number; height: number }, scale: number): React.CSSProperties => ({
    width: `${bounds.width}px`,
    height: `${bounds.height}px`,
    transform: `scale(${scale})`,
  });

  const buildCanvasStyle = (bounds: { width: number; height: number }): React.CSSProperties => ({
    width: `${bounds.width}px`,
    height: `${bounds.height}px`,
  });

  const buildTablePositionStyle = (table: AreaTableView): React.CSSProperties => ({
    left: `${table.positionX}px`,
    top: `${table.positionY}px`,
    width: `${table.width}px`,
    height: `${table.height}px`,
  });

  const floorBackgroundStyle: React.CSSProperties | undefined = floorBackground === 'image'
    ? { backgroundImage: "url('/option2.png')" }
    : floorBackground === 'primary'
      ? { backgroundImage: "url('/option3.png')" }
      : undefined;

  return (
    <section className={layoutClasses.areasSection}>
      <AreaBar
        areas={selectedAreaList}
        activeId={selectedAreaId}
        editing={areaMode === 'edit' && isManager}
        floorBackground={floorBackground}
        isGridVisible={isGridVisible}
        canPickSizeMaster={selectedEditTableIds.length >= 2}
        isPickSizeMasterActive={isSizeMasterPickMode}
        onTogglePickSizeMaster={onToggleSizeMasterPickMode}
        canAlignSelectedTables={selectedEditTableIds.length >= 2}
        onAlignSelectedTablesRow={onAlignSelectedTablesRow}
        onAlignSelectedTablesColumn={onAlignSelectedTablesColumn}
        onSelect={onSelectArea}
        onFloorBackgroundChange={onFloorBackgroundChange}
        onToggleGrid={() => setIsGridVisible((previous) => !previous)}
        onAdd={onAddArea}
        onRename={onRenameArea}
        onToggleFireApproval={(area) => onUpdateAreaFireApproval?.(area.id, !area.requiresFireApproval)}
        onExportArea={onExportArea}
        onImportArea={onImportArea}
        onReorder={onReorderAreas}
        onToggleEnabled={onToggleAreaActive}
        onExitEdit={onExitEditMode}
        onOpenTakeAway={onOpenTakeAway}
        onOpenQuickOrder={onOpenQuickOrder}
      />

      {areaMode === 'view' && (
        <div className={layoutClasses.areaViewContainer}>
          {isPickTableStep && (
            <div className={transferClasses.transferBanner}>
              <span className={transferClasses.transferBannerIcon}>⇌</span>
              <span>
                מעביר משולחן{' '}
                <strong>
                  {(transferMode as { sourceTableLabel: string }).sourceTableLabel}
                </strong>{' '}
                · בחר שולחן יעד
              </span>
              {onCancelTransfer && (
                <div className={transferClasses.mergeBannerActions}>
                  <button type="button" data-testid="pos-areas-transfer-cancel" className={transferClasses.mergeBannerCancel} onClick={onCancelTransfer}>
                    ביטול
                  </button>
                </div>
              )}
            </div>
          )}

          {isRestorePickStep && (
            <div className={transferClasses.transferBanner}>
              <span className={transferClasses.transferBannerIcon}>⟲</span>
              <span>{'בחר שולחן פנוי לשחזור'}</span>
              <div className={transferClasses.mergeBannerActions}>
                <button type="button" data-testid="pos-areas-restore-cancel" className={transferClasses.mergeBannerCancel} onClick={onCancelRestorePick}>
                  {'ביטול'}
                </button>
              </div>
            </div>
          )}

          {isMergePickStep && (
            <div className={transferClasses.mergeBanner}>
              <span className={transferClasses.mergeBannerIcon}>
                <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </span>
              <span>בחר שולחנות למיזוג</span>
              <div className={transferClasses.mergeBannerActions}>
                <button type="button" data-testid="pos-areas-merge-cancel" className={transferClasses.mergeBannerCancel} onClick={onCancelMerge}>
                  ביטול
                </button>
                <button
                  type="button"
                  data-testid="pos-areas-merge-confirm"
                  className={transferClasses.mergeBannerConfirm}
                  disabled={mergeCount < 2}
                  onClick={onConfirmMerge}
                >
                  מיזוג ({mergeCount})
                </button>
              </div>
            </div>
          )}

          {setMinimumMode && (
            <div className={transferClasses.transferBanner}>
              <span className={transferClasses.transferBannerIcon}>₪</span>
              <span>הגדרת מינימום הזמנה · בחר שולחן</span>
              <div className={transferClasses.mergeBannerActions}>
                <UnstyledButton
                  component="button"
                  type="button"
                  data-testid="pos-areas-set-minimum-done"
                  className={transferClasses.mergeBannerConfirm}
                  onClick={onExitSetMinimum}
                >
                  סיום
                </UnstyledButton>
              </div>
            </div>
          )}

          {viewArea && (
            <div
              ref={viewCanvasHostRef}
              className={`${layoutClasses.canvasHost} ${layoutClasses.viewCanvasHost} ${(layoutClasses as Record<string, string>)[`floorBackground${floorBackground.charAt(0).toUpperCase()}${floorBackground.slice(1)}`] ?? ''}`}
              style={floorBackgroundStyle}
              onPointerDown={() => {
                if (justOpenedMenuRef.current) {
                  justOpenedMenuRef.current = false;
                  return;
                }
                setContextMenu(null);
              }}
            >
              <div
                className={layoutClasses.canvasInner}
                data-canvas-inner="true"
                style={buildScaledCanvasStyle(viewAreaCanvasBounds, viewAreaCanvasScale)}
              >
                <div className={layoutClasses.floorDecorLayer} data-testid="pos-areas-decor-layer-view" aria-hidden="true">
                  {currentAreaDecor.map((item) => renderDecorElement(item))}
                </div>
                {viewArea.tables.map((table) => {
                  const order = activeOrders.find((entry) => entry.tableId === table.id) ?? null;
                  const isOccupied = order !== null || activeMergeLinks.some((l) => l.tablePk === table.id);
                  const isSource = sourceTableIds.includes(table.id);
                  const isPickMode = isPickTableStep;
                  const isMergeSelected = mergeSelectedIds.includes(table.id);
                  const isMergeCentral = table.id === mergeCentralTableId;
                  const tableMinimum = resolveEffectiveMinimumOrder(table, order?.dinersCount ?? 1);
                  const isTableMinimumMet = (order?.totalAmount ?? 0) >= tableMinimum;

                  const handleClick = () => {
                    if (contextMenu) {
                      setContextMenu(null);
                      return;
                    }
                    if (setMinimumMode) {
                      setMinTarget(table);
                      return;
                    }
                    if (isRestorePickStep) {
                      // Restore targets an available table only — occupied tables are not pickable.
                      if (!isOccupied && onPickRestoreTable) {
                        onPickRestoreTable(table.id, table.number);
                      }
                      return;
                    }
                    if (isMergePickStep) {
                      if (!isMergeCentral && onToggleMergeTable) {
                        onToggleMergeTable(table.id, table.number);
                      }
                      return;
                    }
                    if (isPickMode && onPickTargetTable) {
                      onPickTargetTable(table.id, table.number);
                      return;
                    }
                    // Step 14: if table is part of an active merge, navigate to the central order's table
                    const mergeLink = activeMergeLinks.find((l) => l.tablePk === table.id);
                    if (mergeLink) {
                      const centralOrder = activeOrders.find((o) => o.id === mergeLink.orderPk);
                      if (centralOrder && centralOrder.tableId !== null && centralOrder.tableId !== table.id) {
                        onViewTableClick(centralOrder.tableId, centralOrder.tableNumber ?? '');
                        return;
                      }
                    }
                    onViewTableClick(table.id, table.number);
                  };

                  return (
                    <Button
                      key={table.id}
                      unstyled
                      type="button"
                      data-testid={`pos-table-${table.id}`}
                      data-occupied={isOccupied ? 'true' : 'false'}
                      className={`
                        ${layoutClasses.viewTableButton}
                        ${table.shape === 'circle' ? layoutClasses.viewTableButtonCircle : layoutClasses.viewTableButtonSquare}
                        ${isOccupied ? layoutClasses.viewTableButtonOccupied : ''}
                        ${isSource ? transferClasses.tableSource : ''}
                        ${isPickMode && !isSource ? transferClasses.tablePickTarget : ''}
                        ${isRestorePickStep && !isOccupied ? transferClasses.tablePickTarget : ''}
                        ${isMergePickStep && isMergeSelected ? transferClasses.tableMergeSelected : ''}
                      `.trim()}
                      onClick={handleClick}
                      onPointerDown={
                        !isPickMode && !isMergePickStep && !isRestorePickStep && !setMinimumMode
                          ? (e) => handleTablePointerDown(e, table, order?.id ?? null)
                          : undefined
                      }
                      onPointerUp={!isPickMode && !isMergePickStep ? handleTablePointerUp : undefined}
                      onPointerLeave={!isPickMode && !isMergePickStep ? () => {
                        if (longPressTimerRef.current) {
                          clearTimeout(longPressTimerRef.current);
                          longPressTimerRef.current = null;
                        }
                      } : undefined}
                      onContextMenu={(event) => event.preventDefault()}
                      // Counter the canvas scale so table numbers stay ~20px on any device.
                      style={{
                        ...buildTablePositionStyle(table),
                        fontSize: `${Math.min(table.height * 0.45, 20 / Math.max(viewAreaCanvasScale, 0.1))}px`,
                      }}
                    >
                      {(() => {
                        const chainLabel = mergeChainLabelByTableId.get(table.id);
                        if (chainLabel) {
                          return (
                            <span className={transferClasses.tableMergeChainLabel}>
                              {chainLabel}
                            </span>
                          );
                        }
                        return table.number;
                      })()}
                      {(order?.hasDeposit || order?.isVip || tableMinimum > 0) && (
                        <span className={layoutClasses.tableBadges}>
                          {order?.hasDeposit && (
                            <span className={`${layoutClasses.tableStatusBadge} ${layoutClasses.tableDepositBadge}`} aria-label="פיקדון">
                              <FontAwesomeIcon icon={faCreditCard as IconProp} />
                            </span>
                          )}
                          {order?.isVip && (
                            <span className={`${layoutClasses.tableStatusBadge} ${layoutClasses.tableVipBadge}`} aria-label="VIP">
                              <FontAwesomeIcon icon={faCrown as IconProp} />
                            </span>
                          )}
                          {tableMinimum > 0 && (
                            <span
                              data-testid={`pos-table-min-order-${table.id}`}
                              className={`${layoutClasses.tableMinOrderBadge} ${isTableMinimumMet ? layoutClasses.tableMinOrderBadgeMet : layoutClasses.tableMinOrderBadgeBelow}`}
                              aria-label={isTableMinimumMet ? 'מינימום הזמנה הושג' : 'מתחת למינימום הזמנה'}
                            >
                              <FontAwesomeIcon icon={faGauge as IconProp} />
                              <span className={layoutClasses.tableMinOrderBadgeAmount}>{formatMoneyCompact(tableMinimum)}</span>
                            </span>
                          )}
                        </span>
                      )}
                      {(isMergePickStep && isMergeSelected) || mergeChainLabelByTableId.has(table.id) ? (
                        <span className={transferClasses.tableMergeIcon} aria-hidden="true">
                          <svg width="0.75em" height="0.75em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                        </span>
                      ) : null}
                    </Button>
                  );
                })}

                {contextMenu && (() => {
                  const contextTableIsMerged = activeMergeLinks.some((l) => l.tablePk === contextMenu.tableId);
                  return (
                    <TableLongPressMenu
                      anchorStyle={contextMenu.style}
                      canvasScale={viewAreaCanvasScale}
                      compact
                      onPrintBill={contextOrderId !== null ? handleContextMenuPrintBill : undefined}
                      onTransfer={contextOrderId !== null ? handleContextMenuTransfer : undefined}
                      transferDisabled={!contextOrderHasSentLines}
                      onCancelTable={contextOrderId !== null ? handleContextMenuCancelTable : undefined}
                      cancelDisabled={!contextOrderHasSentLines}
                      onMerge={!contextTableIsMerged ? handleContextMenuMerge : undefined}
                      onSplit={contextTableIsMerged && onBeginSplitMerge ? handleContextMenuSplit : undefined}
                      onTableHistory={onTableHistoryLongPress ? handleContextMenuTableHistory : undefined}
                      onItemActions={contextOrderId !== null ? handleContextMenuItemActions : undefined}
                      onClose={() => setContextMenu(null)}
                    />
                  );
                })()}

                {viewArea.tables.length === 0 && (
                  <p className={layoutClasses.emptyHint}>אין שולחנות באזור הזה עדיין.</p>
                )}
              </div>
            </div>
          )}
          {!viewArea && !isLoadingAreas && <p>לא נמצאו מתחמים פעילים לטננט הזה.</p>}
        </div>
      )}

      {areaMode === 'edit' && isManager && (
        <div className={layoutClasses.editSection}>
          {editArea && (
            <div
              ref={editCanvasHostRef}
              className={`${layoutClasses.canvasHost} ${layoutClasses.editCanvasHost} ${(layoutClasses as Record<string, string>)[`floorBackground${floorBackground.charAt(0).toUpperCase()}${floorBackground.slice(1)}`] ?? ''}`}
              style={floorBackgroundStyle}
            >
              {isGridVisible && (
                <div className={layoutClasses.editCanvasGrid} data-testid="pos-areas-edit-grid" aria-hidden="true" />
              )}
              <div
                className={layoutClasses.canvasInner}
                data-testid="pos-areas-edit-canvas-inner"
                style={buildCanvasStyle(editAreaCanvasBounds)}
                onMouseDown={(event) => {
                  if (event.button !== 0 || addTableShape || addTableNumber.trim()) {
                    return;
                  }
                  const target = event.target as HTMLElement;
                  const targetTestId = target.getAttribute('data-testid');
                  const clickedDecorLayer = targetTestId === 'pos-areas-decor-layer-edit';
                  if (event.target !== event.currentTarget && !clickedDecorLayer) {
                    return;
                  }
                  const rect = event.currentTarget.getBoundingClientRect();
                  tableSelectionDraggingRef.current = false;
                  setTableSelectionBox({
                    startX: event.clientX - rect.left,
                    startY: event.clientY - rect.top,
                    currentX: event.clientX - rect.left,
                    currentY: event.clientY - rect.top,
                    additive: event.metaKey || event.ctrlKey,
                    originLeft: rect.left,
                    originTop: rect.top,
                  });
                }}
                onClick={(event) => {
                  if (suppressNextCanvasClickRef.current) {
                    suppressNextCanvasClickRef.current = false;
                    return;
                  }
                  setDecorToolbarId(null);
                  if (!addTableShape || !addTableNumber.trim()) {
                    onSelectEditTables?.([], false);
                    return;
                  }
                  // New tables inherit the size of the last-edited table (any move/resize sets editingTableId).
                  const lastEdited = (Array.isArray(editArea.tables) ? editArea.tables : []).find(
                    (table) => table.id === editingTableId,
                  );
                  const width = lastEdited?.width ?? 120;
                  const height = lastEdited?.height ?? 120;
                  const rect = event.currentTarget.getBoundingClientRect();
                  const maxX = Math.max(0, rect.width - width);
                  const maxY = Math.max(0, rect.height - height);
                  const positionX = clamp(event.clientX - rect.left - width / 2, 0, maxX);
                  const positionY = clamp(event.clientY - rect.top - height / 2, 0, maxY);
                  onAddTable(addTableNumber.trim(), addTableShape, positionX, positionY, width, height);
                  setAddTableNumber('');
                }}
              >
                {tableSelectionBox && (
                  <div
                    className={layoutClasses.tableSelectionBox}
                    data-testid="pos-areas-table-selection-box"
                    style={{
                      left: `${Math.min(tableSelectionBox.startX, tableSelectionBox.currentX)}px`,
                      top: `${Math.min(tableSelectionBox.startY, tableSelectionBox.currentY)}px`,
                      width: `${Math.abs(tableSelectionBox.currentX - tableSelectionBox.startX)}px`,
                      height: `${Math.abs(tableSelectionBox.currentY - tableSelectionBox.startY)}px`,
                    }}
                  />
                )}
                {alignmentGuides.vertical !== null && (
                  <div
                    className={`${layoutClasses.tableAlignGuide} ${layoutClasses.tableAlignGuideVertical}`}
                    style={{ left: `${alignmentGuides.vertical}px` }}
                    aria-hidden="true"
                  />
                )}
                {alignmentGuides.horizontal !== null && (
                  <div
                    className={`${layoutClasses.tableAlignGuide} ${layoutClasses.tableAlignGuideHorizontal}`}
                    style={{ top: `${alignmentGuides.horizontal}px` }}
                    aria-hidden="true"
                  />
                )}
                {(alignmentGapHints.horizontal !== null || alignmentGapHints.vertical !== null) && (
                  <div className={layoutClasses.tableGapHint} data-testid="pos-areas-gap-hint" aria-live="polite">
                    {alignmentGapHints.horizontal !== null && <span>H: {alignmentGapHints.horizontal}px</span>}
                    {alignmentGapHints.vertical !== null && <span>V: {alignmentGapHints.vertical}px</span>}
                  </div>
                )}
                <div className={`${layoutClasses.floorDecorLayer} ${layoutClasses.floorDecorLayerEditable}`} data-testid="pos-areas-decor-layer-edit" aria-hidden="true">
                  {currentAreaDecor.map((item) => renderDecorElement(item))}
                </div>
                {(Array.isArray(editArea.tables) ? editArea.tables : []).map((table) => (
                  <div
                    className={`${layoutClasses.editCanvasTable} ${table.shape === 'circle' ? layoutClasses.editCanvasTableCircle : layoutClasses.editCanvasTableSquare} ${selectedEditTableIds.includes(table.id) ? layoutClasses.editCanvasTableSelected : ''}`}
                    key={table.id}
                    data-testid={`pos-areas-edit-table-${table.id}`}
                    onMouseDown={(event) => {
                      if (isSizeMasterPickMode) {
                        event.preventDefault();
                        event.stopPropagation();
                        onPickSizeMasterTable?.(table.id);
                        return;
                      }
                      if (event.metaKey || event.ctrlKey) {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelectEditTable?.(table.id, true);
                        return;
                      }
                      if (!selectedEditTableIds.includes(table.id)) {
                        onSelectEditTable?.(table.id, false);
                      }
                      onBeginCanvasInteraction(event, editArea.id, table, 'move');
                    }}
                    onDoubleClick={() => {
                      onSelectEditTable?.(table.id, false);
                      handleEditTableDoubleClick(table);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelectEditTable?.(table.id, false);
                        onStartEditTable(table);
                        setToolbarTableId(table.id);
                      }
                    }}
                    style={buildTablePositionStyle(table)}
                  >
                    {table.number}
                    {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map((handle) => (
                      <span
                        key={handle}
                        onMouseDown={(event) => onBeginCanvasInteraction(event, editArea.id, table, 'resize', handle)}
                        className={`${layoutClasses.resizeHandle} ${
                          handle === 'nw'
                            ? layoutClasses.resizeHandleNw
                            : handle === 'ne'
                              ? layoutClasses.resizeHandleNe
                              : handle === 'sw'
                                ? layoutClasses.resizeHandleSw
                                : layoutClasses.resizeHandleSe
                        }`}
                      />
                    ))}
                  </div>
                ))}
                {editingTableId !== null && toolbarTableId === editingTableId &&
                  (() => {
                    const selectedTable = (Array.isArray(editArea.tables) ? editArea.tables : []).find(
                      (table) => table.id === editingTableId,
                    );
                    // Wrap the toolbar into two rows only when a side edge would clip the single row.
                    const toolbarCenterX = selectedTable ? selectedTable.positionX + selectedTable.width / 2 : 0;
                    const toolbarCompact = selectedTable
                      ? toolbarCenterX < 110 || editAreaCanvasBounds.width - toolbarCenterX < 110
                      : false;
                    return selectedTable ? (
                      <TableEditToolbar
                        table={selectedTable}
                        compact={toolbarCompact}
                        onRename={onRenameTable}
                        onChangeShape={onChangeShape}
                        onDuplicate={onDuplicateTable}
                        onDelete={onDeleteTable}
                      />
                    ) : null;
                  })()}

                {toolbarDecor && decorToolbarStyle && (
                  <div
                    className={layoutClasses.floorDecorToolbar}
                    data-testid="floor-decor-toolbar"
                    style={decorToolbarStyle}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className={layoutClasses.floorDecorKindRow}>
                      <Button
                        unstyled
                        type="button"
                        className={`${layoutClasses.floorDecorKindButton} ${toolbarDecor.kind !== 'arrow' ? layoutClasses.floorDecorKindButtonActive : ''}`}
                        onClick={() => updateDecorElement(toolbarDecor.id, { kind: 'line' })}
                      >
                        קו
                      </Button>
                      <Button
                        unstyled
                        type="button"
                        className={`${layoutClasses.floorDecorKindButton} ${toolbarDecor.kind === 'arrow' ? layoutClasses.floorDecorKindButtonActive : ''}`}
                        onClick={() => updateDecorElement(toolbarDecor.id, { kind: 'arrow' })}
                      >
                        חץ
                      </Button>
                    </div>
                    <div className={layoutClasses.floorDecorToolbarRow}>
                      <label>
                        צבע
                        <input type="color" value={toolbarDecor.color} data-testid="floor-decor-color" onChange={(event) => updateDecorElement(toolbarDecor.id, { color: event.currentTarget.value })} className={layoutClasses.floorDecorColorInput} />
                      </label>
                      <label>
                        צבע טקסט
                        <input type="color" value={toolbarDecor.textColor ?? '#0f172a'} data-testid="floor-decor-text-color" onChange={(event) => updateDecorElement(toolbarDecor.id, { textColor: event.currentTarget.value })} className={layoutClasses.floorDecorColorInput} />
                      </label>
                      <label>
                        עובי
                        <input type="number" min={1} max={500} value={Math.round(toolbarDecor.stroke)} data-testid="floor-decor-stroke" onChange={(event) => updateDecorElement(toolbarDecor.id, { stroke: clamp(Number(event.currentTarget.value) || 1, 1, 500) })} className={layoutClasses.fieldInput} />
                      </label>
                      <label>
                        טקסט
                        <input type="number" min={10} max={72} value={Math.round(toolbarDecor.fontSize)} data-testid="floor-decor-font-size" onChange={(event) => updateDecorElement(toolbarDecor.id, { fontSize: Math.max(10, Number(event.currentTarget.value) || 10) })} className={layoutClasses.fieldInput} />
                      </label>
                    </div>
                    <label className={layoutClasses.floorDecorTextLabel}>
                      טקסט על האלמנט
                      <input type="text" value={toolbarDecor.text} data-testid="floor-decor-text" onChange={(event) => updateDecorElement(toolbarDecor.id, { text: event.currentTarget.value })} className={layoutClasses.fieldInput} />
                    </label>
                    <div className={layoutClasses.floorDecorToolbarActions}>
                      <Button unstyled type="button" className={layoutClasses.fieldButtonDanger} data-testid="floor-decor-delete" onClick={() => removeDecorElement(toolbarDecor.id)}>מחק אלמנט</Button>
                    </div>
                  </div>
                )}

                {(Array.isArray(editArea.tables) ? editArea.tables : []).length === 0 && (
                  <p className={`${layoutClasses.emptyHint} ${layoutClasses.emptyHintOnDark}`}>
                    אין שולחנות באזור הזה. השתמש ב"הוסף שולחן" כדי ליצור.
                  </p>
                )}
              </div>
              <AddTablePalette
                number={addTableNumber}
                shape={addTableShape}
                onNumberChange={setAddTableNumber}
                onShapeChange={setAddTableShape}
              />
              <section className={layoutClasses.floorDecorPanel} data-testid="pos-areas-floor-decor-panel">
                <div className={layoutClasses.floorDecorAddRow}>
                  <Button unstyled type="button" className={layoutClasses.floorDecorAddButton} data-testid="floor-decor-add-arrow" onClick={() => addDecorElement('arrow')}>
                    <span>חץ</span>
                  </Button>
                  <Button unstyled type="button" className={layoutClasses.floorDecorAddButton} data-testid="floor-decor-add-line" onClick={() => addDecorElement('line')}>
                    <span>קו</span>
                  </Button>
                </div>
              </section>
            </div>
          )}
          {!editArea && <p className={layoutClasses.noAreaSelectedHint}>בחר אזור מהסרגל למעלה כדי לערוך.</p>}
        </div>
      )}

      {mergeConflict && onMergeConflictCancel && onMergeConflictConfirm && (
        <MergeConflictModal
          hasPercentDiscount={mergeConflict.hasPercentDiscount}
          hasPercentTip={mergeConflict.hasPercentTip}
          isLoading={isMergeConflictLoading}
          isManager={isManager}
          onConfirm={onMergeConflictConfirm}
          onCancel={onMergeConflictCancel}
        />
      )}

      {minTarget && (
        <SetMinimumOrderModal
          opened
          tableNumber={minTarget.number}
          dinersCount={activeOrders.find((entry) => entry.tableId === minTarget.id)?.dinersCount ?? 1}
          initialMinimumOrder={minTarget.minimumOrder}
          initialMinimumOrderPerDiner={minTarget.minimumOrderPerDiner}
          isSaving={isSavingMinimum}
          onClose={() => setMinTarget(null)}
          onSave={async (payload) => {
            const saved = await onSaveTableMinimum?.(minTarget.areaId, minTarget.id, payload);
            if (saved !== false) {
              setMinTarget(null);
            }
          }}
        />
      )}

      {splitMergeOrderPk !== null && onCancelSplitMerge && onConfirmSplitMerge && (() => {
        const groupLinks = activeMergeLinks.filter((l) => l.orderPk === splitMergeOrderPk);
        const tableNumberById = new Map<number, string>();
        if (viewArea) {
          for (const t of viewArea.tables) tableNumberById.set(t.id, t.number);
        }
        const groupTables = groupLinks.map((l) => ({
          id: l.tablePk,
          number: tableNumberById.get(l.tablePk) ?? String(l.tablePk),
          linkId: l.id,
        }));
        return (
          <SplitMergeModal
            groupTables={groupTables}
            onConfirm={(linkIds) => onConfirmSplitMerge(linkIds, groupTables.map((table) => ({ tableId: table.id, tableLabel: table.number, linkId: table.linkId })))}
            onCancel={onCancelSplitMerge}
            isLoading={isSplittingMerge}
          />
        );
      })()}
    </section>
  );
};

export const PosAreasSection = React.memo(PosAreasSectionComponent);
